import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Sparkles, AlertTriangle, CheckCircle2, RefreshCw, X, Plus, Trash2 } from 'lucide-react';
import { liteApi } from '@/utils/lite/liteApi';

const STORAGE_KEY = 'playfirst_lite_writing_style';

export function getSavedStyleSummary() {
  try {
    return localStorage.getItem(STORAGE_KEY) || '';
  } catch {
    return '';
  }
}

/**
 * WritingStylePanel — collapsible card that lets a therapist train PlayFirst
 * Lite to mimic their writing voice. Stateless on the server: only the
 * extracted *style guide* (not the samples) is saved in localStorage.
 */
export default function WritingStylePanel({ onChange }) {
  const [open, setOpen] = useState(false);
  const [savedStyle, setSavedStyle] = useState('');
  const [samples, setSamples] = useState(['']);
  const [training, setTraining] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setSavedStyle(getSavedStyleSummary());
  }, []);

  const updateSample = (i, v) => {
    const arr = [...samples];
    arr[i] = v;
    setSamples(arr);
  };
  const addSample = () => samples.length < 3 && setSamples([...samples, '']);
  const removeSample = (i) => samples.length > 1 && setSamples(samples.filter((_, idx) => idx !== i));

  const train = async () => {
    setError('');
    const filled = samples.filter((s) => s.trim().length >= 60);
    if (filled.length === 0) {
      setError('Please paste at least one paragraph (60+ characters).');
      return;
    }
    setTraining(true);
    try {
      const res = await liteApi.extractStyle(filled);
      const summary = (res.style_summary || '').trim();
      try { localStorage.setItem(STORAGE_KEY, summary); } catch {}
      setSavedStyle(summary);
      setSamples(['']);
      setOpen(false);
      onChange?.(summary);
    } catch (e) {
      setError(e.message || 'Could not analyze samples.');
    } finally {
      setTraining(false);
    }
  };

  const clearStyle = () => {
    try { localStorage.removeItem(STORAGE_KEY); } catch {}
    setSavedStyle('');
    onChange?.('');
  };

  return (
    <div
      className="rounded-2xl border border-stone-200 bg-white p-4"
      data-testid="writing-style-panel"
    >
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-3 text-left"
        data-testid="style-panel-toggle"
      >
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-primary" />
          <span className="font-medium text-stone-900">
            Your writing style {savedStyle ? '· active' : '· optional'}
          </span>
        </div>
        <span className="text-stone-400 text-sm">{open ? '▴' : '▾'}</span>
      </button>

      {savedStyle && !open && (
        <div className="mt-3 p-3 rounded-xl bg-emerald-50 border border-emerald-100">
          <div className="flex items-center gap-2 text-sm text-emerald-900 mb-1">
            <CheckCircle2 className="w-4 h-4 text-emerald-600" />
            <span className="font-medium">Style is being applied to all generations</span>
          </div>
          <p className="text-xs text-emerald-800/90 line-clamp-3">
            {savedStyle.substring(0, 200)}{savedStyle.length > 200 ? '…' : ''}
          </p>
          <div className="mt-2 flex gap-2">
            <Button
              type="button" variant="ghost" size="sm"
              onClick={() => setOpen(true)}
              className="rounded-full text-xs h-7"
              data-testid="style-retrain-btn"
            >
              <RefreshCw className="w-3 h-3 mr-1" /> Re-train
            </Button>
            <Button
              type="button" variant="ghost" size="sm"
              onClick={clearStyle}
              className="rounded-full text-xs h-7 text-stone-500"
              data-testid="style-clear-btn"
            >
              <X className="w-3 h-3 mr-1" /> Clear
            </Button>
          </div>
        </div>
      )}

      {open && (
        <div className="mt-4 space-y-4">
          <p className="text-sm text-stone-600 leading-relaxed">
            Train PlayFirst Lite to write reports in <strong>your voice</strong> —
            your terminology, sentence structure, and tone.
          </p>

          <div className="flex items-start gap-2 p-3 rounded-xl bg-amber-50 border border-amber-200 text-sm text-amber-900">
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <p>
              <strong>Only paste DE-IDENTIFIED samples.</strong> Remove child names,
              dates, school names, and any PHI <strong>before</strong> pasting.
              We&rsquo;ll scan and flag identifiers, but you are responsible for
              ensuring samples are de-identified per HIPAA Safe Harbor.
            </p>
          </div>

          {samples.map((s, i) => (
            <div key={i} className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label>Sample {i + 1} {i === 0 ? '(required)' : '(optional)'}</Label>
                {samples.length > 1 && (
                  <Button
                    type="button" variant="ghost" size="icon"
                    onClick={() => removeSample(i)}
                    className="h-7 w-7"
                    data-testid={`style-remove-sample-${i}`}
                  >
                    <Trash2 className="w-3.5 h-3.5 text-stone-400" />
                  </Button>
                )}
              </div>
              <Textarea
                placeholder="Paste a paragraph from a previous report (de-identified)…"
                value={s}
                onChange={(e) => updateSample(i, e.target.value)}
                rows={4}
                data-testid={`style-sample-${i}`}
              />
            </div>
          ))}
          {samples.length < 3 && (
            <Button
              type="button" variant="outline" size="sm"
              onClick={addSample}
              className="rounded-full"
              data-testid="style-add-sample-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> Add another sample
            </Button>
          )}

          {error && <p className="text-sm text-red-600">{error}</p>}

          <p className="text-xs text-stone-500">
            🛡️ Stored in your browser only. The extracted style guide (not your
            samples) is saved to <code>localStorage</code> and never sent to our
            servers after extraction.
          </p>

          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              onClick={train}
              disabled={training}
              className="rounded-full"
              data-testid="style-train-btn"
            >
              {training ? 'Analyzing…' : (savedStyle ? 'Update style' : 'Train my style')}
            </Button>
            <Button
              type="button" variant="outline"
              onClick={() => setOpen(false)}
              className="rounded-full"
              data-testid="style-cancel-btn"
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
